import * as React from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import { alpha, useTheme } from '@mui/material/styles'
import type { CalendarEvent } from '../../typings'
import { moreEventsLabel } from '../../dateFormat'
import { EventTile } from './EventTile'
import { CurrentTimeIndicator } from './CurrentTimeIndicator'
import { COARSE_POINTER, TOUCH_TARGET_PX } from '../touch'
import { BODY_HEIGHT, HOUR_PX, layoutTimedEvents } from './weekGeometry'
import { TimedColumnCreateOverlay } from './TimedColumnCreateOverlay'

type Props = {
  /** The day this column represents (local midnight). */
  day: Date
  /** The timed (non-all-day) events touching this day. */
  events: CalendarEvent[]
  testId: string
  /** Tints the column and draws the current-time line when it is today. */
  highlightToday?: boolean
  /** Lanes before overflow collapses into a "+N" marker. */
  maxLanes?: number
  /** Opens the full list for `day`; without it the "+N" marker is informational. */
  onShowMore?: (day: Date) => void
}

const MARKER_HEIGHT_PX = 20

/**
 * One day's timeline of absolutely-positioned timed tiles, shared by the week
 * body (seven of these) and the day view (one). Overlapping events are laid into
 * side-by-side lanes so none is hidden behind another (LAYOUT-ARCHITECTURE.md §4).
 */
export function TimedEventsColumn({
  day,
  events,
  testId,
  highlightToday,
  maxLanes,
  onShowMore,
}: Props) {
  const theme = useTheme()
  const gridlineBg = `repeating-linear-gradient(${theme.palette.background.default} 0 1px, transparent 1px ${HOUR_PX}px)`

  const { tiles, more } = React.useMemo(
    () => layoutTimedEvents(events, day, maxLanes),
    [events, day, maxLanes],
  )

  const markerSx = {
    position: 'absolute',
    height: MARKER_HEIGHT_PX,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[200],
    fontFamily: theme.typography.fontFamily,
    fontSize: '12px',
    fontWeight: 600,
    color: theme.palette.text.primary,
  } as const

  return (
    <Box
      data-test-id={testId}
      sx={{
        position: 'relative',
        height: `${BODY_HEIGHT}px`,
        minWidth: 0,
        overflow: 'hidden',
        borderLeft: `1px solid ${theme.palette.divider}`,
        backgroundImage: gridlineBg,
        backgroundColor: highlightToday
          ? alpha(theme.palette.primary.main, 0.04)
          : undefined,
      }}
    >
      <TimedColumnCreateOverlay day={day} testId={testId} />
      {tiles.map((p) => (
        <EventTile
          key={p.event.id}
          event={p.event}
          variant="timedWeek"
          geometry={{
            left: p.left,
            right: p.right,
            width: p.width,
            top: p.top,
            height: p.height,
          }}
        />
      ))}
      {more.map((m) => {
        const key = `more-${m.top}-${m.left}`
        const position = { top: m.top, left: m.left, width: m.width }
        return onShowMore ? (
          // The marker sits among tiles, so it can't grow to 44px without
          // covering them; on touch, an invisible overlay extends the hit
          // area to 44px while the visible marker stays 20px.
          <ButtonBase
            key={key}
            data-test-id="timed-more"
            onClick={() => onShowMore(day)}
            aria-label={`${moreEventsLabel(m.count)}, open day view`}
            sx={{
              ...markerSx,
              ...position,
              [COARSE_POINTER]: {
                overflow: 'visible',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: (MARKER_HEIGHT_PX - TOUCH_TARGET_PX) / 2,
                  height: TOUCH_TARGET_PX,
                },
              },
              '&.Mui-focusVisible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            +{m.count}
          </ButtonBase>
        ) : (
          <Box
            key={key}
            data-test-id="timed-more"
            aria-label={moreEventsLabel(m.count)}
            role="note"
            sx={{ ...markerSx, ...position }}
          >
            +{m.count}
          </Box>
        )
      })}
      {highlightToday && <CurrentTimeIndicator day={day} />}
    </Box>
  )
}
